// Base
import React, { useState, useEffect, useRef, useCallback } from "react";

// Types
import type { ReactNode, MouseEvent, CSSProperties } from "react";

// Store
import { useTemplateStore } from "@/zustand/templateStore";

// Components
import CreditCards from "@/components/CreditCards";

// Assets
import { ProductUnchecked, ProductChecked } from "@/assets/Icons";

interface AccordionProps {
  children: ReactNode;
  label?: string;
  image?: string;
  name: string;
  overflow?: boolean;
  maxImgWidth?: number;
  imageHeight?: number;
  isLast?: boolean;
  isFirst?: boolean;
  contrast?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  children,
  label,
  image,
  name,
  isLast,
  isFirst,
  contrast,
}) => {
  const { paymentMethod, handlePaymentMethodChange } = useTemplateStore();
  const [expanded, setExpanded] = useState(false);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const innerContentRef = useRef<HTMLDivElement | null>(null);
  const [currentMaxHeight, setCurrentMaxHeight] = useState(0);

  useEffect(() => setExpanded(name === paymentMethod), [paymentMethod, name]);

  const updateHeight = useCallback(() => {
    if (innerContentRef.current) {
      const height = innerContentRef.current.scrollHeight + 64;
      setCurrentMaxHeight(expanded ? height : 0);
    }
  }, [expanded]);

  useEffect(() => {
    const element = innerContentRef.current;
    if (!element) return;
    requestAnimationFrame(updateHeight);
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [expanded, updateHeight]);

  const handleAccordionClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    handlePaymentMethodChange(name);
  };

  const wrapperStyle: CSSProperties = {
    maxHeight: `${currentMaxHeight}px`,
    overflow: "hidden",
    opacity: expanded ? 1 : 0,
    transition:
      "max-height 0.6s cubic-bezier(0.26,0,0.24,0.99), opacity 0.4s ease-in-out",
  };

  return (
    <div className="mb-0 w-full">
      <div
        onClick={handleAccordionClick}
        className={`flex w-full cursor-pointer items-center justify-between overflow-hidden border p-4 transition-all duration-300 ease-in ${
          expanded && "bg-cc-light-gray-2"
        } ${isFirst && "rounded-t-lg"} ${
          isLast && !expanded && "rounded-b-lg"
        } ${
          contrast && expanded ? "border-cc-violet" : "border-cc-gray-5"
        } ${!expanded && !isLast && "border-b-0"}`}
      >
        <span className="mr-4">
          {expanded ? <ProductChecked /> : <ProductUnchecked />}
        </span>

        {label && (
          <span className="text-cc-gray text-sm font-normal">{label}</span>
        )}

        {name === "card" ? (
          <CreditCards />
        ) : (
          <div className="ml-auto flex items-center">
            <img
              src={image}
              alt="Payments"
              className={
                name === "googlepay"
                  ? "-my-7 h-20 min-h-6 w-22 translate-x-4 transform"
                  : "max-h-6 w-auto object-contain"
              }
            />
          </div>
        )}
      </div>

      <section ref={contentRef} aria-expanded={expanded} style={wrapperStyle}>
        <div
          ref={innerContentRef}
          className={`bg-cc-light-gray flex flex-wrap border-r border-l p-3 ${
            isLast && expanded ? "rounded-b-lg border-b" : ""
          } ${contrast ? "border-cc-gray-5" : "border-cc-gray-4"}`}
        >
          {children}
        </div>
      </section>
    </div>
  );
};

export default Accordion;
